// src/redux/services/store/storeMenusApi.ts
import type { EndpointBuilder } from "@reduxjs/toolkit/query";
import { baseApi } from "../baseApi";

export type StoreMenuItem = {
  id: number;
  menu_id: number;
  parent_id: number | null;
  sort_order: number;
  is_active: boolean;

  label: string;
  item_type: "route" | "url" | "page" | "divider";
  to_path?: string | null;
  url?: string | null;
  page_id?: number | null;

  meta_json?: Record<string, any> | null;

  group_key?: string | null;
  group_title?: string | null;
  column_no?: number | null;
  display_as?: string | null;

  children?: StoreMenuItem[];
};

export type StoreMenuBlockItem = {
  id: number;
  block_id: number;
  sort_order: number;
  is_active: boolean;
  title?: string | null;
  image_url?: string | null;
  to_path?: string | null;
  url?: string | null;
  meta_json?: Record<string, any> | null;
};

export type StoreMenuBlock = {
  id: number;
  menu_id: number;
  block_key: string;
  block_type: string;
  sort_order: number;
  is_active: boolean;

  title?: string | null;
  subtitle?: string | null;
  image_url?: string | null;
  overlay_json?: Record<string, any> | null;

  cta_label?: string | null;
  cta_to_path?: string | null;
  cta_url?: string | null;
  cta_style?: string | null;
  cta_icon?: string | null;

  html_content?: string | null;
  settings_json?: Record<string, any> | null;

  items?: StoreMenuBlockItem[];
};

export type StoreMenuResponse = {
  menu_key: string;
  title?: string | null;
  location?: string | null;
  template_type?: string | null;
  is_active?: boolean;

  items: StoreMenuItem[];
  blocks?: StoreMenuBlock[];
};

export type GetStoreMenuArgs = {
  menuKey: string;
  activeOnly?: boolean;
  buildTree?: boolean;
  includeBlocks?: boolean;
};

// ✅ Force builder type so "builder" is not any even if TS thinks baseApi is any
type B = EndpointBuilder<any, any, any>;

export const storeMenusApi = baseApi.injectEndpoints({
  endpoints: (builder: B) => ({
    getStoreMenu: builder.query<StoreMenuResponse, GetStoreMenuArgs>({
      query: ({ menuKey, activeOnly = true, buildTree = true, includeBlocks = true }) => ({
        url: `/store/menus/${encodeURIComponent(menuKey)}`,
        params: {
          active_only: activeOnly,
          build_tree: buildTree,
          include_blocks: includeBlocks,
        },
        method: "GET",
      }),
      providesTags: (_result: StoreMenuResponse | undefined, _err: unknown, arg: GetStoreMenuArgs) => [
        { type: "StoreMenu", id: arg.menuKey },
      ],
    }),
  }),
  overrideExisting: false,
});

export const { useGetStoreMenuQuery } = storeMenusApi;